import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const BuildLessonAroundTopic = () => {
  const title = 'Build a Lesson Around a Topic';
  const image = '/Images/2people1.webp';
  const intro = 'Topic-based lessons give students a meaningful context for language use. Instead of teaching grammar in isolation, you anchor everything—vocabulary, skills, functions—around a theme that motivates and connects. This guide walks you through designing a coherent, engaging topic-based lesson from start to finish.';

  const seoContent = [];

  const contentSections = [
    {
      heading: '1. Select & Frame the Topic',
      body: 'Start with learner needs analysis (see "Know Your Students"). Map topics to their goals: Business English → meetings, negotiations, emails, presentations. Academic → lectures, seminars, essays, citations. General → travel, culture, technology, health. Frame the topic as a question or problem: "How is AI changing our jobs?" beats "Artificial Intelligence." Questions create cognitive need.',
    },
    {
      heading: '2. Map Language to the Topic',
      body: 'Create a topic web: center = topic; branches = vocabulary sets, grammar points, functions, skills, genres. For "Sustainable Living": Vocab = carbon footprint, upcycle, renewable, single-use; Grammar = present perfect for changes, modals for obligation/recommendations, conditionals for future scenarios; Functions = persuading, comparing, suggesting; Skills = reading infographics, discussing solutions, writing a pledge.',
    },
    {
      heading: '3. Choose & Scaffold Input',
      body: 'Select 1–2 rich texts (article, video, podcast, infographic) at i+1. Pre-teach 5–7 blocking items only. Design while-viewing/reading tasks: gist → detail → inference → personal response. Provide scaffolds: glossaries, graphic organizers, transcript segments, replay options. The input fuels the output.',
    },
    {
      heading: '4. Design the Communicative Output Task',
      body: 'The final task must be authentic, outcome-driven, and require the target language. Examples: "Design a zero-waste challenge for your neighborhood" (sustainability), "Pitch a remote-work policy to management" (workplace), "Plan a 3-day itinerary for a visitor" (travel). Specify: role, audience, format, success criteria, time limit.',
    },
    {
      heading: '5. Plan the Language Focus',
      body: 'Extract 2–3 high-yield language points from the input and output. Teach them inductively: learners notice patterns in the text → teacher clarifies form/meaning/pronunciation → controlled practice → freer practice in task context. Avoid grammar-for-grammar\'s-sake; every focus serves the topic task.',
    },
    {
      heading: '6. Build in Recycling & Reflection',
      body: 'End with a 5-minute reflection: "What new words/phrases will you use this week?" "What was challenging?" Schedule a spaced-review task in 3 days (re-tell, rewrite, quiz). Connect to next lesson\'s topic for continuity.',
    },
  ];

  const exampleTopics = [
    {
      topic: 'Remote Work & Digital Nomads',
      level: 'B1–B2',
      grammar: 'Present perfect (experience), future forms (predictions), modals (policy)',
      vocab: 'Hybrid, asynchronous, burnout, timezone, coworking',
      task: 'Design a company remote-work policy; present to "leadership"',
    },
    {
      topic: 'Food Culture & Identity',
      level: 'A2–B1',
      grammar: 'Count/non-count, quantifiers, passive (is made with), relative clauses',
      vocab: 'Staple, ferment, fusion, comfort food, heritage',
      task: 'Create a video script introducing your culture\'s signature dish',
    },
    {
      topic: 'Green Cities',
      level: 'B2–C1',
      grammar: 'Conditionals (solutions), causative (have/get done), inversion (Not only...)',
      vocab: 'Urban planning, congestion, green corridor, retrofit, circular economy',
      task: 'Town hall simulation: debate a car-free city center proposal',
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={[]}
      seoTitle="Build a Lesson Around a Topic: Topic-Based ESL Lesson Design Guide"
      seoDescription="Complete guide to designing topic-based English lessons. Topic selection criteria, language mapping, input scaffolding, communicative task design, inductive grammar focus, and three worked examples (Remote Work, Food Culture, Green Cities)."
      datePublished="2026-10-18"
      dateModified="2026-10-18"
    >
      <div className="teach-content">
        {contentSections.map((section, i) => (
          <section key={i} className="content-section">
            <h2>{section.heading}</h2>
            <p>{section.body}</p>
          </section>
        ))}

        <section className="content-section worked-examples">
          <h2>Worked Examples</h2>
          <div className="example-grid">
            {exampleTopics.map((ex, i) => (
              <article key={i} className="example-card">
                <h3>{ex.topic}</h3>
                <p className="example-level"><strong>Level:</strong> {ex.level}</p>
                <p className="example-grammar"><strong>Grammar:</strong> {ex.grammar}</p>
                <p className="example-vocab"><strong>Key Vocab:</strong> {ex.vocab}</p>
                <p className="example-task"><strong>Final Task:</strong> {ex.task}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="content-section checklist">
          <h2>Topic-Based Lesson Checklist</h2>
          <ul>
            <li>Topic is relevant, generative, and framed as a question/problem</li>
            <li>Language map created (vocab, grammar, functions, skills, genres)</li>
            <li>Input text(s) selected at i+1 with scaffolds prepared</li>
            <li>While-input tasks progress: gist → detail → inference → response</li>
            <li>Final communicative task is authentic, outcome-driven, criterion-referenced</li>
            <li>2–3 language foci extracted from input/output, taught inductively</li>
            <li>Differentiation planned: tiered texts, tiered tasks, flexible grouping</li>
            <li>Assessment rubric matches task (task completion, language, interaction)</li>
            <li>Recycling activity scheduled (3-day, 1-week, 2-week)</li>
            <li>Connection to next topic/lesson articulated</li>
          </ul>
        </section>
      </div>
    </TeachEnglishPageLayout>
  );
};

export default BuildLessonAroundTopic;